import { apiData } from "./api";
import { cartData } from "./cartData";
import { Errored } from "./errors";

/**
 * Checks if any value in the data object is a Blob or File.
 * @param {Object} data - The data object to check.
 * @returns {boolean} True if a Blob/File is found, false otherwise.
 */
function containsFiles(data) {
  for (const key in data) {
    if (Object.hasOwn(data, key)) {
      if (data[key] instanceof Blob || data[key] instanceof File) {
        return true;
      }
      // Optionally check nested objects/arrays if needed
    }
  }
  return false;
}

/**
 * Executes HTTP requests (POST, GET, PUT, PATCH, DELETE). Determines Content-Type based on data and method.
 *
 * Retrieves shopping cart items, extracts references, and adds them as 'cartReferences' array to the data
 * before sending the request (relevant for POST/PUT/PATCH).
 * If data contains Files/Blobs and method is POST/PUT/PATCH, sends as 'multipart/form-data'.
 * Otherwise, sends as 'application/json' for POST/PUT/PATCH.
 * For GET/DELETE, data is sent as query parameters.
 *
 * @param {Object} data - The original data to be sent in the request or used as query params.
 * @param {string} path - The API endpoint path.
 * @param {string} [method='POST'] - The HTTP method (e.g., 'POST', 'GET', 'PUT', 'PATCH', 'DELETE').
 * @param {string} [type] - Optional responseType for axios.
 * @returns {Promise<any>} The result of the request.
 */
export async function runData(data, path, method = 'POST', type) {
  const requestData = { ...data };
  const axiosMethod = method.toUpperCase(); // Normalize method

  // Add cart item references only for methods that typically send a body
  if (['POST', 'PUT', 'PATCH'].includes(axiosMethod)) {
      try {
        const cartItems = await cartData("list");
        if (Array.isArray(cartItems) && cartItems.length > 0) {
            // Rename the key to cartReferences
            requestData.cartReferences = cartItems.map((item) => item.reference).filter(ref => ref);
        } else {
            // Ensure cartReferences key exists but is empty if no items or error
            requestData.cartReferences = [];
        }
      } catch (error) {
        console.error("Error retrieving cart data for request:", error);
        // Ensure cartReferences key exists but is empty on error
        requestData.cartReferences = [];
      }
      // Remove the old 'shopping' key if it exists from the original data
      // to avoid sending it accidentally if it wasn't meant for the cart.
      delete requestData.shopping;
  } else {
      // For GET/DELETE, remove cartReferences if it was accidentally passed in data
      delete requestData.cartReferences;
      // Also remove shopping if present
      delete requestData.shopping;
  }

  const config = type ? { responseType: type } : {};
  let payload;
  let useFormData = containsFiles(requestData) && ['POST', 'PUT', 'PATCH'].includes(axiosMethod);

  if (useFormData) {
    // Use FormData if files are present
    payload = new FormData();
    for (const key in requestData) {
      if (Object.hasOwn(requestData, key)) {
        // FormData handles Blobs directly. Stringify non-Blob objects.
        if (typeof requestData[key] === "object" && !(requestData[key] instanceof Blob) && requestData[key] !== null) {
           // Special handling for arrays like 'cartReferences' in FormData
           if (Array.isArray(requestData[key])) {
               // Handle cartReferences array specifically if needed, or use general array handling
               if (key === 'cartReferences') {
                   // Option 1: Send as stringified JSON (often easier for backend)
                   payload.append(key, JSON.stringify(requestData[key]));
                   // Option 2: Send as multiple entries (if backend expects this format)
                   // requestData[key].forEach((item, index) => {
                   //     payload.append(`${key}[${index}]`, item);
                   // });
               } else {
                   // Handle other arrays (like the actual 'shopping' field if it's ever sent with files)
                   payload.append(key, JSON.stringify(requestData[key]));
               }
           } else {
               payload.append(key, JSON.stringify(requestData[key]));
           }
        } else if (requestData[key] !== null && requestData[key] !== undefined) {
          payload.append(key, requestData[key]);
        }
      }
    }
    // When using FormData, Axios usually sets the Content-Type automatically.
    // Do not manually set config.headers['Content-Type'] = 'multipart/form-data';
  } else if (['POST', 'PUT', 'PATCH'].includes(axiosMethod)) {
    // Use JSON payload for POST/PUT/PATCH if no files
    payload = requestData; // This now includes cartReferences instead of shopping
    config.headers = { ...(config.headers || {}), 'Content-Type': 'application/json' };
  } else {
    // For GET/DELETE, payload is used for params
    payload = requestData; // Keep data for params (cartReferences was removed earlier)
  }

  try {
    const methodLower = axiosMethod.toLowerCase();
    if (!apiData[methodLower]) {
        throw new Error(`Unsupported HTTP method: ${axiosMethod}`);
    }

    if (['post', 'put', 'patch'].includes(methodLower)) {
        return await apiData[methodLower](path, payload, config);
    } else { // Handles GET, DELETE, HEAD, OPTIONS
        config.params = payload; // Assign data to params for GET/DELETE etc.
        return await apiData[methodLower](path, config);
    }

  } catch (error) {
    let errorMessage, statusCode;

    if (type === "blob") {
      errorMessage =
        "An error occurred while processing your request, please contact support.";
      statusCode = 500; // Use 500 for blob processing errors
    } else {
      errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred.";
      // Use the status from the response if available, otherwise default to 500
      statusCode = error.response?.status || 500;
    }

    throw new Errored(errorMessage, statusCode);
  }
}
